"use client";

import React from "react";
import Image from "next/image";
import GlobalForm from "@/components/forms/GlobalForm";
import {
  SuccessMessageType,
  VerticalCategory,
  FinanceAndAccountingSubcategory,
  FormHeading,
} from "@/interface/types";
import { useInactivityPopup } from "@/hooks/useInactivityPopup";
import { useModalScrollLock } from "@/hooks/useModalScrollLock";

interface NewInactivityPopupProps {
  course?: string;
  courseId?: number;
  verticalName?: VerticalCategory;
  subCategory?: string;
  pageName?: string;
  delay?: number;
}

const NewInactivityPopup: React.FC<NewInactivityPopupProps> = ({
  course = "CMA",
  courseId = 2,
  verticalName = VerticalCategory.FINANCE_AND_ACCOUNTING,
  subCategory = FinanceAndAccountingSubcategory.CMA,
  pageName = "cma-visit",
  delay = 5000,
}) => {
  const { showPopup, hidePopup } = useInactivityPopup({
    delay,
    sessionStorageKey: `inactivity-popup-${pageName}`,
  });

  useModalScrollLock(showPopup, {
    restoreScrollPosition: true,
    preventTouchScroll: true,
  });

  const handleClose = () => {
    sessionStorage.removeItem(`inactivity-popup-${pageName}`);
    sessionStorage.removeItem("user-interacted-form");
    localStorage.removeItem(`inactivity-popup-${pageName}`);
    hidePopup();
  };

  return (
    <>
      {showPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex justify-center items-center z-[999] lg:p-10 p-4">
          <div
            className="rounded-2xl shadow-lg relative overflow-hidden mx-auto bg-white w-full max-w-[370px] md:w-[615px] md:max-w-[615px]"
            style={{
              boxShadow: "0px 0px 14px 0px rgba(4, 84, 175, 0.29)",
            }}
          >
            <button
              onClick={handleClose}
              aria-label="Close"
              className="text-[#3399ff] text-sm md:text-sm absolute right-2 top-2 md:right-2 md:top-2 cursor-pointer w-8 h-8 md:w-8 md:h-8 flex items-center justify-center rounded-full border border-[#3399ff] bg-white z-30"
            >
              ✕
            </button>

            <div className="flex gap-5 md:gap-10">
              <div
                className="relative z-10 p-6 md:w-[70%] w-full
                  [&_input:not([type='checkbox']):not(.PhoneInputInput)]:!rounded-[10px] [&_input:not([type='checkbox']):not(.PhoneInputInput)]:!border [&_input:not([type='checkbox']):not(.PhoneInputInput)]:!border-[#E4E4E4] [&_input:not([type='checkbox']):not(.PhoneInputInput)]:!bg-[#F3F5F8]
                  [&_select]:!rounded-[10px] [&_select]:!border [&_select]:!border-[#E4E4E4] [&_select]:!bg-[#F3F5F8]
                  [&_.PhoneInput]:!rounded-[10px] [&_.PhoneInput]:!border [&_.PhoneInput]:!border-[#E4E4E4] [&_.PhoneInput]:!bg-[#F3F5F8] [&_.PhoneInput]:!px-3
                  [&_.PhoneInputCountry]:!bg-transparent [&_.PhoneInputCountry]:!border-0 [&_.PhoneInputCountry]:!rounded-none [&_.PhoneInputCountry]:!px-0 [&_.PhoneInputCountry]:!mr-2
                  [&_.PhoneInputCountrySelect]:!bg-transparent [&_.PhoneInputCountrySelect]:!border-0
                  [&_.PhoneInputInput]:!bg-transparent [&_.PhoneInputInput]:!border-0 [&_.PhoneInputInput]:!rounded-none [&_.PhoneInputInput]:!px-0 [&_.PhoneInputInput]:!text-[#595858] [&_.PhoneInputInput::placeholder]:!text-[#595858]
                  [&_input[type='checkbox']:checked]:!bg-[#2A85FF]
                  [&_button[type='submit']]:!rounded-[20px_20px_20px_0] [&_button[type='submit']]:!bg-[#39f]
                  has-[input:placeholder-shown]:[&_button[type='submit']]:!bg-[#9CB6DD]"
              >
                <GlobalForm
                  course={course}
                  courseId={courseId}
                  formType="Flash popup"
                  isAStepperForm={true}
                  pageType="accounting page"
                  subCategory={subCategory}
                  submitButtonName="Register for Webinar"
                  successMessage={SuccessMessageType.ACCOUNTING}
                  verticalName={verticalName}
                  darkBackground={false}
                  formHeading={
                    FormHeading.CONNECT_WITH_A_CMA_EXPERT_FOR_DETAILS
                  }
                />
              </div>
              <div className="w-[30%] hidden md:block">
                <Image
                  src="https://ik.imagekit.io/mileseducation/miles_website/home_new/form-girl.webp"
                  alt="AI-Ready Accountant"
                  width={229}
                  height={316}
                  className="absolute right-0 bottom-0 z-0"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default NewInactivityPopup;
